import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import ListCreateButton from 'components/buttons/ListCreateButton';
import { DraftNameCell } from 'components/drafts/DraftNameCell';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import { WorkspacePremiumOutlined as WorkspacePremiumOutlinedIcon } from 'components/icons';
import type { DataTableColumnDef } from 'components/tables/data-table';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import { useMemo } from 'react';

import { getClassifierText, t } from 'utils/getTranslations';

import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import { useLazyGetJobPositionsWithFiltersQuery } from 'store/api/jobPositions';
import { FeatureFlag } from 'types/features';
import { JobPositionDto } from 'types/jobPositions';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

const USED_CLASSIFIERS = ['JOB_POSITION_TYPE'];

const getJobPositionHref = (jobPosition: JobPositionDto) => `/job-position/${jobPosition.id}`;

const JobPositionsTable: React.FC<Record<string, never>> = () => {
    const navigateWithReturn = useNavigateWithReturnState();
    const [fetchJobPositions, { data, isFetching }] = useLazyGetJobPositionsWithFiltersQuery();
    const fetchData = useWithDrafts(fetchJobPositions);
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const isJobPositionTypeFeatureEnabled = useIsFeatureEnabled(FeatureFlag.JOB_POSITION_TYPE);

    const cols: DataTableColumnDef<JobPositionDto>[] = [
        {
            header: t('job-positions.public-name'),
            id: 'name',
            accessorKey: 'name',
            meta: {
                widthWeight: 1,
                minPx: 180,
            },
            cell: ({ cell }) => {
                const { id, name, isDraft } = cell.row.original;
                return id ? (
                    <DraftNameCell draft={isDraft ? { type: 'job-position', id } : null}>
                        <LinkWithOrigin
                            className="entity-link font-semibold hover:underline"
                            to={`/job-position/${id}`}
                        >
                            {name || (isDraft ? t('new-entity.job-position') : '-')}
                        </LinkWithOrigin>
                    </DraftNameCell>
                ) : (
                    '-'
                );
            },
        },
        ...(isJobPositionTypeFeatureEnabled
            ? ([
                  {
                      header: t('job-positions.position-type'),
                      id: 'positionTypeCode',
                      accessorKey: 'positionTypeCode',
                      meta: {
                          widthWeight: 1,
                          minPx: 160,
                      },
                      cell: ({ getValue }) => {
                          const value = getValue<JobPositionDto['positionTypeCode']>();
                          return value ? getClassifierText(classifiers['JOB_POSITION_TYPE'], value) : '-';
                      },
                  },
              ] as DataTableColumnDef<JobPositionDto>[])
            : []),
        {
            header: t('job-positions.current-employees'),
            accessorKey: 'currentEmployees',
            // Not sortable: a per-row aggregate count with no backing DB column to order by.
            enableSorting: false,
            meta: {
                widthWeight: 1,
                minPx: 140,
            },
            cell: ({ getValue }) => getValue<JobPositionDto['currentEmployees']>() ?? '',
        },
    ];

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: [{ selector: 'name' }, { selector: 'positionTypeCode' }],
        }),
        []
    );

    return (
        <ListTableWithFilters<JobPositionDto>
            getRowHref={getJobPositionHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="job-positions"
            columns={cols}
            data={data}
            isLoading={isFetching || isLoadingClassifiers}
            pluralTextId="job-positions.job-positions"
            singularTextId="job-positions.job-positions"
            noEntriesId="job-positions.no-job-positions-found"
            noEntriesBodyId="job-positions.empty.body"
            noEntriesIcon={WorkspacePremiumOutlinedIcon}
            noEntriesAction={
                <ListCreateButton
                    permissions={['CREATE.JOB_POSITION']}
                    variant="contained"
                    color="secondary"
                    onClick={() => navigateWithReturn('/job-position/new')}
                    labelId="job-positions.add-new-job-position"
                    newLabelId="new-entity.job-position"
                />
            }
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
        />
    );
};

export default JobPositionsTable;
